import { useEffect, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { PrimaryButton, TextField } from "./ui";
import { JurisdictionField } from "./JurisdictionField";
import { useAppState } from "../AppStateContext";
import { api, type Project } from "../api";
import { isValidDomain, toAsciiDomain } from "../domain";
import { jurisdictionCreateCode } from "../jurisdiction";
import { useJurisdictions } from "../useJurisdictions";
import { useI18n } from "../i18n";
import "./Dialog.css";

export function NewProjectDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (project: Project) => void;
}) {
  const { createProject, deleteProject } = useAppState();
  const { t } = useI18n();
  const { options: jurisdictions } = useJurisdictions();
  const [name, setName] = useState("");
  const [host, setHost] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hostTouched, setHostTouched] = useState(false);

  const hostInvalid = host.trim() !== "" && !isValidDomain(host);
  const inlineHostError = hostTouched && hostInvalid
    ? t("onboarding.siteInvalid")
    : null;

  useEffect(() => {
    if (open) return;
    const id = window.setTimeout(() => {
      setName("");
      setHost("");
      setJurisdiction("");
      setError(null);
      setHostTouched(false);
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setHostTouched(true);
    const cleanHost = host.trim().replace(/^https?:\/\//, "").replace(/\/$/, "");
    const cleanName = name.trim() || cleanHost;
    if (!cleanHost) {
      setError(t("newProject.invalidHost"));
      return;
    }
    // Validate BEFORE creating the project — otherwise a malformed domain
    // would leave an orphan empty project behind every time the user retries.
    if (!isValidDomain(cleanHost)) {
      setError(t("onboarding.siteInvalid"));
      return;
    }
    const asciiHost = toAsciiDomain(cleanHost);
    setBusy(true);
    setError(null);
    const jurisdictionCode = jurisdictionCreateCode({
      selected: jurisdiction,
      enabledCodes: jurisdictions.map((j) => j.code),
    });
    let project;
    try {
      project = await createProject({
        name: cleanName,
        description: cleanHost,
        jurisdiction_code: jurisdictionCode,
      });
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : t("newProject.createFailed"));
      return;
    }
    let createdTargetId: string | null = null;
    try {
      const created = await api.addScopeTarget(project.project_id, asciiHost);
      createdTargetId = created.target_id;
    } catch (err) {
      // Target add failed after project was created — roll back so retries
      // don't pile up orphan projects.
      try {
        await deleteProject(project.project_id);
      } catch {
        /* best-effort cleanup */
      }
      setBusy(false);
      setError(err instanceof Error ? err.message : t("newProject.createDomainFailed"));
      return;
    }
    // Kick off DNS verification right away so OnboardingPage opens with the
    // real TXT token instead of the placeholder, and so the user's first
    // "I added the record" click won't 409 because /verification/start was
    // never called. Best-effort: if this fails, OnboardingPage's auto-loader
    // will retry — no need to roll back the project.
    try {
      await api.startVerifyTarget(createdTargetId, "dns");
    } catch {
      /* recovered by OnboardingPage auto-loader */
    }
    setBusy(false);
    onCreated?.(project);
  };

  // Portal to <body>: the page's .anim-page transform forms the containing
  // block for `position: fixed`, which would offset this backdrop from the
  // viewport (same fix as ConfirmDialog).
  return createPortal(
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="dialog__head">
          <h2>{t("newProject.title")}</h2>
          <button type="button" aria-label={t("common.close")} onClick={onClose}>
            ×
          </button>
        </header>
        <form className="dialog__body" onSubmit={onSubmit}>
          <TextField
            placeholder={t("newProject.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <TextField
            placeholder={t("newProject.hostPlaceholder")}
            value={host}
            onChange={(e) => setHost(e.target.value)}
            onBlur={() => setHostTouched(true)}
            aria-invalid={hostInvalid ? true : undefined}
          />
          {jurisdictions.length > 0 ? (
            <JurisdictionField
              options={jurisdictions}
              value={jurisdiction}
              onChange={setJurisdiction}
              placeholder={t("jurisdiction.placeholder")}
            />
          ) : null}
          {inlineHostError ? (
            <p className="auth-error">{inlineHostError}</p>
          ) : error ? (
            <p className="auth-error">{error}</p>
          ) : null}
          <div className="dialog__actions">
            <button type="button" className="btn--outline" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <PrimaryButton
              type="submit"
              className="btn--auto"
              disabled={busy || !host.trim() || hostInvalid}
            >
              {busy ? t("newProject.createBusy") : t("common.create")}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
